import { PermissionEnum } from '@Application/Security/Roles';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import DeleteAiCostLimitDialog from '@UI/Pages/Administration/AiCostLimits/ConfirmationDialogs/DeleteAiCostLimitDialog';
import AccountLimit from '@UI/Pages/Administration/AiCostLimits/Sections/AccountLimit';
import LevelLimits from '@UI/Pages/Administration/AiCostLimits/Sections/LevelLimits';
import UserLimits from '@UI/Pages/Administration/AiCostLimits/Sections/UserLimits';
import AiCostLimitSidebar from '@UI/Pages/Administration/Shared/Sidebars/AiCostLimitSidebar';
import useAiCostLimitSubjects from '@UI/Hooks/useAiCostLimitSubjects';
import useAiCostLimits from '@UI/Pages/Administration/Shared/Hooks/useAiCostLimits';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import { css } from '@emotion/react';
import * as React from 'react';
import { Skeleton } from '@verclam/ui-kit';

const contentStyle = css`
	display: flex;
	flex-direction: column;
	gap: 2rem;
`;

/** Loads the limits and the limitable subjects of the current account, then renders one section per level. */
const AiCostLimitsContent: React.FC = () => {
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	const { data: limits, error: limitsError, refetch: refetchLimits } = useAiCostLimits(accountId);
	const {
		data: subjects,
		error: subjectsError,
		refetch: refetchSubjects,
	} = useAiCostLimitSubjects(accountId);

	const handleRetry = React.useCallback(() => {
		refetchLimits();
		refetchSubjects();
	}, [refetchLimits, refetchSubjects]);

	if (accountId === null) {
		return <EmptyState title="Aucun compte n'est rattaché à votre profil." />;
	}

	if (limitsError !== null || subjectsError !== null) {
		return <ErrorState title="Impossible de charger les plafonds." onRetry={handleRetry} />;
	}

	if (
		limits === null ||
		limits.accountId !== accountId ||
		subjects === null ||
		subjects.accountId !== accountId
	) {
		return (
			<Skeleton height="16rem" borderRadius={theme.radii.xlarge} animation="wave" shape="rectangle" />
		);
	}

	return (
		<div css={contentStyle}>
			<AccountLimit />
			<LevelLimits
				level="organization"
				title="Structures"
				emptyText="Aucun plafond par structure."
				addLabel="Ajouter un plafond"
				users={null}
			/>
			<LevelLimits
				level="facility"
				title="Agences"
				emptyText="Aucun plafond par agence."
				addLabel="Ajouter un plafond"
				users={null}
			/>
			<WithPermission permission={PermissionEnum.USER_READ_ACCOUNT}>
				<UserLimits accountId={accountId} />
			</WithPermission>
			<AiCostLimitSidebar accountId={accountId} />
			<DeleteAiCostLimitDialog />
		</div>
	);
};

export default AiCostLimitsContent;
